import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRightLeft } from 'lucide-react'
import { cn } from '@/utils/cn'
import { getStageLabel, stageConfig } from './deals-constants'
import { checkTransition, getAllowedTransitions } from '@/services/dealTransition.service'
import type { Deal, DealStage } from '@/types'
import { useAuthStore } from '@/stores/authStore'

type KanbanMoveStageMenuProps = {
  deal: Deal
  onMoveStage: (dealId: string, stage: DealStage) => void
}

export function KanbanMoveStageMenu({ deal, onMoveStage }: KanbanMoveStageMenuProps) {
  const { user } = useAuthStore()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const targets = useMemo(() => getAllowedTransitions(deal.etapa, user?.role), [deal.etapa, user?.role])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  if (targets.length === 0) return null

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-md border border-border/80',
          'bg-bg-card/90 text-text-muted opacity-0 transition-opacity',
          'group-hover/card:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
          open && 'opacity-100 ring-2 ring-accent/30',
        )}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Mover ${deal.empresaNome} para outra etapa`}
        title="Mover para outra etapa"
      >
        <ArrowRightLeft size={14} aria-hidden />
      </button>

      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 min-w-[200px] max-h-56 overflow-y-auto rounded-lg border border-[var(--glass-border)] bg-[var(--bg-card)] py-1 shadow-xl"
        >
          {targets.map((stage) => {
            const check = checkTransition(deal, stage, user?.role)
            const color = stageConfig[stage]?.color
            return (
              <li key={stage} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={!check.allowed}
                  title={check.reason}
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpen(false)
                    if (check.allowed) onMoveStage(deal.id, stage)
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-xs',
                    check.allowed
                      ? 'text-text-primary hover:bg-white/[0.06]'
                      : 'cursor-not-allowed text-text-muted opacity-50',
                  )}
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: color }}
                    aria-hidden
                  />
                  {getStageLabel(stage)}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
